import { useThree } from '@react-three/fiber';
import { useDrag } from '@use-gesture/react';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

import { useRayVisualization } from '../hooks';
import { findMiddleIntersectionPoint } from '../utils';
import { MarkerTooltip } from './MarkerTooltip';
import { SlotBelowCanvas } from './SlotBelowCanvas';

export function DraggableMarker({
  id,
  targetObject,
  onWorldPositionChange,
  onScreenPositionChange,
  defaultWorldPosition,
  screenPosition,
  isDragging,
  isError,
  name,
  color = '#65F157',
  showRay = false,
}: {
  id: string;
  targetObject: THREE.Object3D;
  onWorldPositionChange: (pos: THREE.Vector3 | undefined) => void;
  onScreenPositionChange: (pos: THREE.Vector2, isDraggin: boolean) => void;
  defaultWorldPosition?: THREE.Vector3;
  screenPosition?: THREE.Vector2;
  isDragging: boolean;
  isError: boolean;
  name?: string;
  color?: string;
  showRay?: boolean;
}) {
  const threeState = useThree();
  const circleRef = useRef<HTMLDivElement | null>(null);
  const raycaster = useRef(new THREE.Raycaster());
  const pointer = useRef(new THREE.Vector2());
  const initialPositioningDone = useRef(false);
  const boundsRef = useRef<HTMLDivElement | null>(null);
  const onMoveRef = useRef((e: PointerEvent) =>
    dispatchClonedEvent(e, threeState.gl.domElement),
  );

  // Use the ray visualization hook with explicit initial visibility
  const { updateRayLine } = useRayVisualization({
    color,
    visible: showRay,
  });

  const setInitialCirclePosition = () => {
    if (!circleRef.current) return;

    if (defaultWorldPosition) {
      const canvas = threeState.gl.domElement;
      const rect = canvas.getBoundingClientRect();

      const screenPos = projectWorldToScreen(
        defaultWorldPosition,
        threeState.camera,
        rect,
      );

      circleRef.current.style.left = `${screenPos.x}px`;
      circleRef.current.style.top = `${screenPos.y}px`;
    } else {
      // Find element with ID matching name
      const targetElement = document.getElementById(id);
      if (targetElement) {
        const targetRect = targetElement.getBoundingClientRect();
        const canvas = threeState.gl.domElement;
        const canvasRect = canvas.getBoundingClientRect();

        const left = targetRect.left + targetRect.width / 2 - canvasRect.left;
        const top = targetRect.top - canvasRect.top + targetRect.height / 2;

        circleRef.current.style.left = `${left}px`;
        circleRef.current.style.top = `${top}px`;
      }
    }

    initialPositioningDone.current = true;

    // Initialize ray position after setting initial position
    if (defaultWorldPosition) {
      // Instead of manually setting ray origin and direction, use setFromCamera
      // to ensure consistent behavior with orthographic camera

      // Project the world position to normalized device coordinates
      const vector = defaultWorldPosition.clone().project(threeState.camera);

      // Set the pointer to these normalized coordinates
      pointer.current.x = vector.x;
      pointer.current.y = vector.y;

      // Use setFromCamera to create a ray consistent with the camera type
      raycaster.current.setFromCamera(pointer.current, threeState.camera);

      // Update the ray visualization
      updateRayLine(raycaster.current);
    }
  };

  const setCircleRef = (element: HTMLDivElement | null) => {
    circleRef.current = element;
    if (element && !initialPositioningDone.current) {
      setInitialCirclePosition();

      // Bind movement to dispatch it toward canvas
      element.addEventListener('pointermove', onMoveRef.current);
    }
  };

  const checkIntersectionAndSetCoordinates = (x: number, y: number) => {
    const canvas = threeState.gl.domElement;
    const rect = canvas.getBoundingClientRect();
    const normalizedPos = getNormalizedPointerCoordinates(x, y, rect);
    pointer.current.x = normalizedPos.x;
    pointer.current.y = normalizedPos.y;

    raycaster.current.setFromCamera(pointer.current, threeState.camera);

    // raycaster.current.params.Mesh = {
    //   threshold: 1.0,
    //   firstHitOnly: false,
    // };

    targetObject.updateMatrixWorld(true);

    if (!targetObject.visible) {
      onWorldPositionChange(undefined);
      return;
    }

    const intersects = raycaster.current.intersectObject(targetObject, true);

    const point = findMiddleIntersectionPoint(intersects);
    onWorldPositionChange(point);

    // Update ray visualization using the hook
    updateRayLine(raycaster.current);
  };

  const bindDrag = useDrag(
    ({ event, first, active }) => {
      if (!event) return;
      const { clientX, clientY } = event as MouseEvent;

      // Just update the visual position during drag
      const canvas = threeState.gl.domElement;
      const rect = canvas.getBoundingClientRect();
      const canvasPos = getCanvasRelativePosition(clientX, clientY, rect);

      onScreenPositionChange(canvasPos, active);
    },
    {
      pointer: { capture: false },
      filterTaps: true,
      threshold: 0,
      eventOptions: { passive: false },
      bounds: boundsRef,
    },
  );

  const isOverCanvas = (): boolean => {
    if (circleRef.current) {
      const elmRect = circleRef.current.getBoundingClientRect();
      const canRect = threeState.gl.domElement.getBoundingClientRect();

      return (
        elmRect.x >= canRect.x &&
        elmRect.x <= canRect.x + canRect.width &&
        elmRect.y >= canRect.y &&
        elmRect.y <= canRect.y + canRect.height
      );
    }
    return false;
  };

  // #region HOOKS
  // Cleanup the Dispatch event onMove
  // Note: Needed by ZoomScopeView since this element blocks canvas from registering it
  useEffect(() => {
    return () =>
      // eslint-disable-next-line react-hooks/exhaustive-deps
      circleRef.current?.removeEventListener('pointermove', onMoveRef.current);
  }, []);

  // Update ray visibility when showRay changes
  useEffect(() => {
    // Force an update of the ray if it's now visible
    if (showRay && raycaster.current) {
      updateRayLine(raycaster.current);
    }
  }, [showRay, updateRayLine, id]);

  // Update circle position & run intersection
  useEffect(() => {
    if (screenPosition && circleRef.current) {
      circleRef.current.style.left = `${screenPosition.x}px`;
      circleRef.current.style.top = `${screenPosition.y}px`;

      // Only perform ray casting when dragging is done ( pointer up )
      if (!isDragging) {
        // Only test when over canvas
        if (isOverCanvas()) {
          checkIntersectionAndSetCoordinates(
            screenPosition.x,
            screenPosition.y,
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenPosition, isDragging]);

  // #endregion

  return (
    <SlotBelowCanvas.In>
      <div
        className="pointer-events-none absolute left-0 top-0 h-full w-full"
        ref={boundsRef}
      >
        <div
          ref={setCircleRef}
          {...bindDrag()}
          className="group pointer-events-auto absolute z-10 flex h-[30px] w-[30px] -translate-x-1/2 -translate-y-1/2 transform cursor-move touch-none select-none items-center justify-center rounded-full border-[3px] bg-white/20 hover:z-11"
          style={{ borderColor: color }}
          draggable={false}
        >
          <MarkerTooltip name={name ?? ''} showError={isError}>
            <div className="border-gray-90 h-[5px] w-[5px] rounded-full border bg-white transition-opacity duration-200 ease-in-out" />
          </MarkerTooltip>
        </div>
      </div>
    </SlotBelowCanvas.In>
  );
}

function projectWorldToScreen(
  worldPosition: THREE.Vector3,
  camera: THREE.Camera,
  rect: DOMRect,
): { x: number; y: number } {
  const vector = new THREE.Vector3(
    worldPosition.x,
    worldPosition.y,
    worldPosition.z,
  );
  const widthHalf = rect.width / 2;
  const heightHalf = rect.height / 2;

  vector.project(camera);

  return {
    x: vector.x * widthHalf + widthHalf,
    y: -(vector.y * heightHalf) + heightHalf,
  };
}

function getCanvasRelativePosition(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): THREE.Vector2 {
  return new THREE.Vector2(clientX - rect.left, clientY - rect.top);
}

function getNormalizedPointerCoordinates(
  canvasX: number,
  canvasY: number,
  rect: DOMRect,
): { x: number; y: number } {
  return {
    x: (canvasX / rect.width) * 2 - 1,
    y: -(canvasY / rect.height) * 2 + 1,
  };
}

// Clone an event then dispatch it to an HTML Element
function dispatchClonedEvent(e: Event, elm: HTMLElement) {
  if (e.type.startsWith('pointer')) {
    const evt = e as PointerEvent;
    elm.dispatchEvent(
      new PointerEvent(evt.type, {
        bubbles: evt.bubbles,
        cancelable: evt.cancelable,
        clientX: evt.clientX,
        clientY: evt.clientY,
        button: evt.button,
        ctrlKey: evt.ctrlKey,
        shiftKey: evt.shiftKey,
        altKey: evt.altKey,
        metaKey: evt.metaKey,
      }),
    );
  } else {
    console.error('Can not dispatch cloned event, event type unknown');
  }
}
